"use client"

import { addToCart } from "@lib/data/cart"
import { ProductBySku } from "@lib/data/products"
import { convertToLocale } from "@lib/util/money"
import { Button, Text } from "@modules/common/components/ui"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Modal from "@modules/common/components/modal"
import Image from "next/image"
import { useParams } from "next/navigation"
import { useState } from "react"

type UpsellModalProps = {
  isOpen: boolean
  close: () => void
  // Pre-filtered: excludes the product being viewed and out-of-stock items.
  products: ProductBySku[]
}

// Post-add-to-cart upsell: offers the matched shroud/fans/relay/accessories
// as one-click adds (each entry maps to a specific variant_id).
export default function UpsellModal({
  isOpen,
  close,
  products,
}: UpsellModalProps) {
  const countryCode = useParams().countryCode as string

  const [addedVariantIds, setAddedVariantIds] = useState<string[]>([])
  const [addingVariantId, setAddingVariantId] = useState<string | null>(null)

  const hasHoseFilter = products.some((p) =>
    p.matched_sku.startsWith("CCHF")
  )

  const handleAdd = async (item: ProductBySku) => {
    setAddingVariantId(item.variant_id)

    try {
      await addToCart({
        variantId: item.variant_id,
        quantity: 1,
        countryCode,
      })
      setAddedVariantIds((prev) => [...prev, item.variant_id])
    } catch {
      // Leave the button in its default state so the user can retry.
    } finally {
      setAddingVariantId(null)
    }
  }

  return (
    <Modal isOpen={isOpen} close={close} data-testid="upsell-modal">
      <Modal.Title>Complete Your Cooling System</Modal.Title>
      <div className="flex flex-col gap-y-2 pt-3">
        <Text className="text-small-regular text-ui-fg-subtle">
          Don&apos;t wait until your radiator arrives to realize the matched
          shroud, fans, and relay are a no-brainer — they ship together in one
          box.
        </Text>
        {hasHoseFilter && (
          <Text className="text-small-regular text-ui-fg-subtle">
            Keep your engine and radiator cleaner for longer with an inline
            hose filter designed for our radiators — check your coolant
            quality at a glance.
          </Text>
        )}
      </div>
      <ul className="flex flex-col divide-y divide-gray-100 mt-4 overflow-y-auto">
        {products.map((item) => {
          const isAdded = addedVariantIds.includes(item.variant_id)

          return (
            <li
              key={item.variant_id}
              className="flex items-center gap-x-4 py-3"
              data-testid="upsell-item"
            >
              <div className="relative w-16 h-16 shrink-0 rounded-md bg-white border border-gray-200 overflow-hidden">
                {item.thumbnail && (
                  <Image
                    src={item.thumbnail}
                    alt={item.title}
                    className="object-contain object-center p-1"
                    sizes="64px"
                    fill
                  />
                )}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <Text className="text-small-regular text-ui-fg-base truncate">
                  {item.title}
                </Text>
                {item.price != null && item.currency_code && (
                  <Text className="text-small-regular font-semibold text-ui-fg-base">
                    {convertToLocale({
                      amount: item.price,
                      currency_code: item.currency_code,
                    })}
                  </Text>
                )}
              </div>
              <Button
                variant={isAdded ? "secondary" : "primary"}
                size="small"
                className="shrink-0 min-w-[72px]"
                disabled={isAdded}
                isLoading={addingVariantId === item.variant_id}
                onClick={() => handleAdd(item)}
                data-testid="upsell-add-button"
              >
                {isAdded ? (
                  <span className="flex items-center gap-x-1">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 10.5 8.5 15 16 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Added
                  </span>
                ) : (
                  "Add"
                )}
              </Button>
            </li>
          )
        })}
      </ul>
      <div className="flex items-center justify-between gap-x-4 border-t border-gray-100 pt-4 mt-2">
        <Button
          variant="transparent"
          size="small"
          onClick={close}
          data-testid="upsell-dismiss-button"
        >
          No thanks, continue shopping
        </Button>
        <LocalizedClientLink
          href="/cart"
          className="text-small-regular font-medium underline text-ui-fg-base"
          data-testid="upsell-view-cart-link"
        >
          View cart
        </LocalizedClientLink>
      </div>
    </Modal>
  )
}
